import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faCalendarXmark,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "../css/PauseStatusBanner.css";

/**
 * Computes the simulation status display info (icon, class, text, message)
 * from the given pause/profile state. Exported for reuse in collapsed
 * header badges.
 */
export function getSimulationStatusDisplay({
  isPaused,
  hasActiveSchedule,
  profileStatus,
  pauseEndDate,
  pauseDurationDays,
  isDemoMode = false,
  t = (key) => key,
}) {
  // Demo mode always overrides server-reported status
  if (isDemoMode) {
    return {
      icon: faCalendarXmark,
      statusClass: "no-schedule",
      statusText: t("sps.status.demo.text"),
      message: (
        <>
          {t("sps.status.demo.messagePart1")}
          <br />
          {t("sps.status.demo.messagePart2")}
        </>
      ),
    };
  }

  // Profile-level pause (no active subscription)
  if (profileStatus === "PAUSED") {
    return {
      icon: faPause,
      statusClass: "paused",
      statusText: t("sps.status.paused.text"),
      message: t("sps.status.paused.noSub"),
    };
  }

  // No active schedule state
  if (hasActiveSchedule === false) {
    return {
      icon: faCalendarXmark,
      statusClass: "no-schedule",
      statusText: t("sps.status.scheduling.text"),
      message: t("sps.status.scheduling.message"),
    };
  }

  if (!isPaused) {
    return {
      icon: faPlay,
      statusClass: "active",
      statusText: t("sps.status.active.text"),
      message: t("sps.status.active.message"),
    };
  }

  // Paused state
  let message = t("sps.status.paused.base");

  if (pauseEndDate) {
    const endDate = new Date(pauseEndDate);
    const today = new Date();
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

    if (daysRemaining > 0) {
      message = t("sps.status.paused.until").replace(
        "{date}",
        formatDateOnly(pauseEndDate, "medium"),
      );
    } else {
      message = t("sps.status.paused.ended");
    }
  } else if (pauseDurationDays) {
    message = t("sps.status.paused.days").replace(
      "{days}",
      pauseDurationDays,
    );
  } else {
    message = t("sps.status.paused.indefinitely");
  }

  return {
    icon: faPause,
    statusClass: "paused",
    statusText: t("sps.status.paused.text"),
    message,
  };
}

/**
 * PauseStatusBanner Component
 *
 * Displays the current pause status of simulations with quick action buttons.
 * Shows prominently at the top of the Simulation Profile Settings.
 */
function PauseStatusBanner({
  isPaused,
  hasActiveSchedule,
  profileStatus,
  pauseEndDate,
  pauseDurationDays,
  pauseReason,
  onPause,
  onResume,
  isDemoMode = false,
}) {
  const { t } = useTranslation();
  const status = getSimulationStatusDisplay({
    isPaused,
    hasActiveSchedule,
    profileStatus,
    pauseEndDate,
    pauseDurationDays,
    isDemoMode,
    t,
  });
  const showActions = hasActiveSchedule !== false && profileStatus !== "PAUSED";

  return (
    <div className={`pause-status-banner ${status.statusClass}`}>
      <div className="pause-status-content">
        <div className="pause-status-icon">
          <FontAwesomeIcon icon={status.icon} />
        </div>

        <div className="pause-status-info">
          <div className="pause-status-header">
            <span className="pause-status-label">{t("sps.status.label")}</span>
            <span className="pause-status-value">{status.statusText}</span>
          </div>
          <div className="pause-status-message">{status.message}</div>
          {isPaused && pauseReason && (
            <div className="pause-status-reason">
              <span className="reason-label">{t("sps.status.reasonLabel")}</span> {pauseReason}
            </div>
          )}
        </div>

        {showActions && (
          <div className="pause-status-actions">
            {isPaused ? (
              <button
                className="pause-action-button resume"
                onClick={onResume}
                title={
                  isDemoMode
                    ? t("sps.status.resumeDisabled")
                    : t("sps.status.resumeTitle")
                }
                disabled={isDemoMode}
              >
                <FontAwesomeIcon icon={faPlay} />
                <span>{t("sps.status.resume")}</span>
              </button>
            ) : (
              <button
                className="pause-action-button pause"
                onClick={onPause}
                title={
                  isDemoMode
                    ? t("sps.status.pauseDisabled")
                    : t("sps.status.pauseTitle")
                }
                disabled={isDemoMode}
              >
                <FontAwesomeIcon icon={faPause} />
                <span>{t("sps.status.pause")}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PauseStatusBanner;
